import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
  Badge,
  Card,
  CardHeader,
  CardBody,
  CardText,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import SelectorGrupos from "../../common/SelectorGrupos";
import moment from "moment";

const VENTANA_DIAS = 90;

function ReporteVariacionInventario() {
  const { user } = useContext(AuthContext);
  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;

  const [productos, setProductos] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const ITEMS_PER_PAGE = 50;

  const [categorias, setCategorias] = useState([]);
  const [selectedCategorias, setSelectedCategorias] = useState([]); // multi-seleccion (vacio = todas)
  const [selectedAlerta, setSelectedAlerta] = useState("");
  const [cargando, setCargando] = useState(false);

  // Grupos de clientes elegidos antes de generar el reporte
  const [gruposSel, setGruposSel] = useState([]);
  const [gruposInfo, setGruposInfo] = useState([]);
  const [modalGrupos, setModalGrupos] = useState(true);

  const toggleCategoria = (id) => {
    setSelectedCategorias((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setCurrentPage(1);
  };

  const Toast = Swal.mixin({
    toast: true,
    position: "center",
    showConfirmButton: false,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
  });

  if (cargando) {
    Toast.fire({
      html: `<i class="fas fa-hourglass-half custom-spin fa-2x" style="margin-right: 16px;"></i>`,
      title: "Danos unos segundos....",
      customClass: { icon: "animated-icon" },
    });
  }

  useEffect(() => {
    if (!gruposSel.length) return;
    setCargando(true);
    const q = `?grupos=${gruposSel.join(",")}`;
    axios
      .get(`${URL_DASHBOARDS}VariacionInventario${q}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        let data = response.data;
        setProductos(data);
        let unicas = data.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.idCategoria === value.idCategoria)
        );
        setCategorias(unicas);
        setCargando(false);
        setCurrentPage(1);
        Toast.close();
      })
      .catch((err) => {
        console.log(err);
        setCargando(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruposSel]);

  const confirmarGrupos = (ids, grupos) => {
    setGruposSel(ids);
    setGruposInfo(grupos);
    setModalGrupos(false);
  };

  // Actualiza el valor local de dias mientras escribe
  function handleChangeDias(idArticulo, value) {
    setProductos((prev) =>
      prev.map((p) =>
        p.idArticulo === idArticulo ? { ...p, DiasProduccion: value } : p
      )
    );
  }

  // Guarda los dias de produccion/entrega del articulo
  function handleSaveDias(idArticulo, value) {
    axios
      .post(
        `${URL_DASHBOARDS}DiasProduccion`,
        { idArticulo, dias: Number(value) || 0 },
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      )
      .catch((err) => {
        console.log(err);
        Swal.fire({ icon: "error", title: "Oops...", text: "No se pudo guardar los dias" });
      });
  }

  function calcula(p) {
    const vendidas = parseFloat(p.UnidadesVendidas) || 0;
    const inventario = parseFloat(p.Inventario) || 0;
    const promedioDiario = vendidas / VENTANA_DIAS;
    const diasCubiertos = promedioDiario > 0 ? inventario / promedioDiario : null;
    // Dias de produccion/entrega POR ITEM
    const lead = parseFloat(p.DiasProduccion) || 0;
    // Dias antes de tener que recomprar (para no quedar sin stock en el lead time)
    let diasParaRecomprar = diasCubiertos === null ? null : diasCubiertos - lead;
    const comprar = diasCubiertos !== null && diasCubiertos <= lead;
    let fechaRecompra = "-";
    if (diasCubiertos !== null) {
      const dias = Math.max(0, Math.round(diasParaRecomprar));
      fechaRecompra = moment().add(dias, "days").format("YYYY-MM-DD");
    }
    return {
      ...p,
      PromedioDiario: promedioDiario,
      DiasCubiertos: diasCubiertos,
      FechaRecompra: fechaRecompra,
      Alerta: comprar ? "Comprar" : "OK",
    };
  }

  const calculados = useMemo(() => {
    let lista = productos.map(calcula);
    if (selectedCategorias.length > 0)
      lista = lista.filter((e) => selectedCategorias.includes(String(e.idCategoria)));
    if (selectedAlerta) lista = lista.filter((e) => e.Alerta === selectedAlerta);
    if (search) {
      lista = lista.filter(
        (e) =>
          (e.Codigo || "").toLowerCase().includes(search.toLowerCase()) ||
          (e.Nombre || "").toLowerCase().includes(search.toLowerCase())
      );
    }
    return lista;
  }, [productos, selectedCategorias, selectedAlerta, search]);

  // KPIs
  const kpis = useMemo(() => {
    const total = calculados.length;
    const comprar = calculados.filter((p) => p.Alerta === "Comprar").length;
    const conVenta = calculados.filter((p) => p.UnidadesVendidas > 0).length;
    return { total, comprar, conVenta };
  }, [calculados]);

  const headers = [
    { name: "Item", field: "Codigo", sortable: true },
    { name: "Nombre", field: "Nombre", sortable: true },
    { name: "Categoria", field: "Categoria", sortable: true },
    { name: "Vendidas 90d", field: "UnidadesVendidas", sortable: true },
    { name: "Prom. diario", field: "PromedioDiario", sortable: true },
    { name: "Inventario", field: "Inventario", sortable: true },
    { name: "Dias cubiertos", field: "DiasCubiertos", sortable: true },
    { name: "Dias prod/entrega", field: "DiasProduccion", sortable: true },
    { name: "Fecha recompra", field: "FechaRecompra", sortable: false },
    { name: "Alerta", field: "Alerta", sortable: true },
  ];

  const commentsData = useMemo(() => {
    let computed = [...calculados];
    const numFields = ["UnidadesVendidas", "PromedioDiario", "Inventario", "DiasCubiertos"];
    if (sorting.field && !numFields.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = computed.sort(
        (a, b) => reversed * (a[sorting.field] || "").localeCompare(b[sorting.field] || "")
      );
    }
    if (sorting.field && numFields.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = computed.sort((a, b) => {
        const av = a[sorting.field] === null ? Infinity : parseFloat(a[sorting.field]);
        const bv = b[sorting.field] === null ? Infinity : parseFloat(b[sorting.field]);
        return reversed * (av - bv);
      });
    }
    setTotalItems(computed.length);
    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [calculados, currentPage, sorting]);

  function excel() {
    const data = calculados.map((a) => ({
      Item: a.Codigo,
      Nombre: a.Nombre,
      Categoria: a.Categoria,
      VendidasUlt90Dias: a.UnidadesVendidas,
      PromedioDiario: a.PromedioDiario.toFixed(2),
      Inventario: a.Inventario,
      DiasCubiertos: a.DiasCubiertos === null ? "Sin venta" : a.DiasCubiertos.toFixed(0),
      DiasProduccionEntrega: a.DiasProduccion,
      FechaRecompra: a.FechaRecompra,
      Alerta: a.Alerta,
    }));
    const filtro = ["Item", "Nombre", "Categoria", "VendidasUlt90Dias", "PromedioDiario", "Inventario", "DiasCubiertos", "DiasProduccionEntrega", "FechaRecompra", "Alerta"];
    var option = {};
    option.fileName = "ReporteVariacionInventario";
    option.datas = [{ sheetData: data, sheetName: "Variacion", sheetFilter: filtro, sheetHeader: filtro }];
    new ExportJsonExcel(option).saveExcel();
  }

  function PDFTabla() {
    const data = calculados.map((a) => [
      a.Codigo,
      a.Nombre,
      new Intl.NumberFormat("en-US").format(a.UnidadesVendidas),
      a.PromedioDiario.toFixed(2),
      new Intl.NumberFormat("en-US").format(a.Inventario),
      a.DiasCubiertos === null ? "Sin venta" : a.DiasCubiertos.toFixed(0),
      a.DiasProduccion,
      a.FechaRecompra,
      a.Alerta,
    ]);
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 120, 15, 45, 15);
    doc.text(`Reporte de Variacion de Inventario`, 20, 30);
    doc.autoTable({
      head: [["Item", "Nombre", "Vendidas 90d", "Prom. diario", "Inventario", "Dias cubiertos", "Dias prod/entrega", "Fecha recompra", "Alerta"]],
      body: data,
      startY: 40,
      didParseCell: function (d) {
        if (d.section === "body") {
          const fila = calculados[d.row.index];
          if (fila && fila.Alerta === "Comprar") {
            d.cell.styles.textColor = [198, 40, 40];
            d.cell.styles.fontStyle = "bold";
          }
        }
      },
    });
    doc.save(`ReporteVariacionInventario.pdf`);
  }

  return (
    <>
      <Header />
      <SelectorGrupos
        isOpen={modalGrupos}
        onConfirm={confirmarGrupos}
        onClose={() => setModalGrupos(false)}
        allowClose={gruposSel.length > 0}
        initialSelected={gruposSel}
      />
      <br />
      <br />
      {user.menu_reportes ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/ReportesBI" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
              <h3>Reporte de Variacion de Inventario</h3>
              <small>
                Demanda (ventas) de los ultimos {VENTANA_DIAS} dias de los grupos
                elegidos. El inventario es global (stock de bodega).
              </small>
              <div>
                <small style={{ color: "#555" }}>
                  Grupos: <b>{gruposInfo.map((g) => g.name).join(", ") || "—"}</b>{" "}
                </small>
                <Button
                  size="sm"
                  color="link"
                  className="p-0"
                  onClick={() => setModalGrupos(true)}
                >
                  Cambiar grupos
                </Button>
              </div>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <Row>
            <Col md={5}>
              <Label className="mr-sm-2">
                Categorias{" "}
                {selectedCategorias.length > 0 && (
                  <small
                    style={{ color: "#1565c0", cursor: "pointer" }}
                    onClick={() => setSelectedCategorias([])}
                  >
                    (limpiar)
                  </small>
                )}
              </Label>
              <div
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  maxHeight: "110px",
                  overflowY: "auto",
                  padding: "6px 10px",
                }}
              >
                {categorias
                  .slice()
                  .sort((a, b) => (a.Categoria > b.Categoria ? 1 : -1))
                  .map((a) => (
                    <label
                      key={a.idCategoria}
                      style={{ display: "inline-block", marginRight: 16, fontWeight: "normal", cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategorias.includes(String(a.idCategoria))}
                        onChange={() => toggleCategoria(String(a.idCategoria))}
                        style={{ marginRight: 5, verticalAlign: "middle" }}
                      />
                      <span style={{ verticalAlign: "middle" }}>{a.Categoria}</span>
                    </label>
                  ))}
              </div>
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Alerta</Label>
              <Input bsSize="sm" type="select" value={selectedAlerta}
                onChange={(e) => { setSelectedAlerta(e.target.value); setCurrentPage(1); }}>
                <option value="">Todas</option>
                <option value="Comprar">Comprar</option>
                <option value="OK">OK</option>
              </Input>
            </Col>
            <Col md={4} className="d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </Col>
          </Row>
          <br />

          {/* KPIs */}
          <Row style={{ justifyContent: "center" }}>
            <Col md={3}>
              <Card className="text-center" style={{ borderTop: "4px solid #1565c0" }}>
                <CardHeader tag="h6">Productos</CardHeader>
                <CardBody><CardText tag="h3">{kpis.total}</CardText></CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                className="text-center"
                style={{
                  borderTop: "4px solid #c62828",
                  cursor: "pointer",
                  boxShadow: selectedAlerta === "Comprar" ? "0 0 0 2px #c62828" : "none",
                }}
                onClick={() =>
                  setSelectedAlerta(selectedAlerta === "Comprar" ? "" : "Comprar")
                }
                title="Clic para filtrar solo los productos con alerta de compra"
              >
                <CardHeader tag="h6">Alerta "Comprar"</CardHeader>
                <CardBody>
                  <CardText tag="h3" style={{ color: "#c62828", fontWeight: "bold" }}>{kpis.comprar}</CardText>
                  <small style={{ color: "#c62828" }}>
                    {selectedAlerta === "Comprar" ? "Filtrando ✓ (clic para quitar)" : "clic para filtrar"}
                  </small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center" style={{ borderTop: "4px solid #2e7d32" }}>
                <CardHeader tag="h6">Con ventas (90d)</CardHeader>
                <CardBody><CardText tag="h3">{kpis.conVenta}</CardText></CardBody>
              </Card>
            </Col>
          </Row>
          <br />

          <Pagination
            total={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader headers={headers} onSorting={(field, order) => setSorting({ field, order })} />
            <tbody>
              {commentsData.map((a) => {
                const rojo = a.Alerta === "Comprar";
                return (
                  <tr key={a.idArticulo} style={rojo ? { color: "#c62828" } : {}}>
                    <td>{a.Codigo}</td>
                    <td>{a.Nombre}</td>
                    <td>{a.Categoria}</td>
                    <td>{new Intl.NumberFormat("en-US").format(a.UnidadesVendidas)}</td>
                    <td>{a.PromedioDiario.toFixed(2)}</td>
                    <td>{new Intl.NumberFormat("en-US").format(a.Inventario)}</td>
                    <td>{a.DiasCubiertos === null ? "Sin venta" : a.DiasCubiertos.toFixed(0)}</td>
                    <td style={{ minWidth: "90px" }}>
                      <Input
                        bsSize="sm"
                        type="number"
                        value={a.DiasProduccion ?? ""}
                        onChange={(e) => handleChangeDias(a.idArticulo, e.target.value)}
                        onBlur={(e) => handleSaveDias(a.idArticulo, e.target.value)}
                        style={{ width: "80px" }}
                      />
                    </td>
                    <td>{a.FechaRecompra}</td>
                    <td>
                      {rojo ? (
                        <Badge color="danger">Comprar</Badge>
                      ) : (
                        <Badge color="success">OK</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}
      <br />
      {loader}
    </>
  );
}

export default ReporteVariacionInventario;
